import { toast } from 'react-toastify';
import { putApi } from '../../Repository/Api';
import endPoints from '../../Repository/apiConfig';
import { parseTimesheetLocalDate } from './timesheetTimeUtils';

const GRACE_PERIOD_MS = 24 * 60 * 60 * 1000;

const COMPLETE_ERROR_PATTERNS = [
  'Staff must start the job before it can be marked completed.',
  'Too early to complete',
  'Too late to complete',
];

const showActionResult = (data, fallbackSuccess, fallbackError) => {
  const message = data?.msg || data?.message;

  if (data?.success) {
    toast.success(message || fallbackSuccess);
    return true;
  }

  toast.error(message || fallbackError);
  return false;
};

export const getAdminCompleteWindow = toTime => {
  const endDate = parseTimesheetLocalDate(toTime);

  if (!endDate) {
    return { canComplete: false, reason: 'Scheduled end time is not available.' };
  }

  const endTime = endDate.getTime();
  const now = Date.now();
  const windowEnd = endTime + GRACE_PERIOD_MS;

  if (now < endTime) {
    return {
      canComplete: false,
      reason:
        'Too early to complete. Admin can mark completed from the scheduled end time onward.',
      opensAt: new Date(endTime),
      closesAt: new Date(windowEnd),
    };
  }

  if (now > windowEnd) {
    return {
      canComplete: false,
      reason:
        'Too late to complete. Admin can mark completed up to 24 hours after the scheduled end time.',
      closesAt: new Date(windowEnd),
    };
  }

  return {
    canComplete: true,
    reason: null,
    opensAt: new Date(endTime),
    closesAt: new Date(windowEnd),
  };
};

export const getApprovalLabel = item => {
  if (item?.userApprovedAt) return 'Approved';
  if (item?.approvalRequired !== true) return 'Not required';
  if (item?.jobStatus === 'Started') return 'Awaiting approval';
  return 'Pending (not started)';
};

export const getApprovalColor = item => {
  if (item?.userApprovedAt) return '#00AD1D';
  if (item?.approvalRequired !== true) return '#8A8A8A';
  if (item?.jobStatus === 'Started') return '#FFA800';
  return '#E67E22';
};

export const canApproveStaffJob = item =>
  item?.approvalRequired === true &&
  item?.status === 'Accepted' &&
  item?.jobStatus === 'Started' &&
  !item?.userApprovedAt;

export const canCompleteStaffJob = item => {
  if (item?.jobStatus !== 'Started') return false;
  return getAdminCompleteWindow(item?.toTime).canComplete;
};

export const getCompleteBlockReason = item => {
  if (!item) return '';
  if (item?.jobStatus === 'Completed') return 'This shift is already completed.';
  if (item?.jobStatus !== 'Started') {
    return 'Staff must start the job before it can be marked completed.';
  }
  return getAdminCompleteWindow(item?.toTime).reason || '';
};

export const approveStaffJob = async (staffBookingId, { onSuccess } = {}) => {
  const data = await putApi(endPoints.approveStaffJob(staffBookingId), {}, {
    errorMsg: 'Failed to approve staff shift!',
  });

  if (!data) return false;

  const ok = showActionResult(
    data,
    'Staff shift approved successfully by admin.',
    'Failed to approve staff shift!'
  );

  if (ok && onSuccess) onSuccess();
  return ok;
};

export const completeStaffJob = async (staffBookingId, { onSuccess } = {}) => {
  const data = await putApi(
    endPoints.updatedTimesheetById(staffBookingId),
    { jobStatus: 'Completed' },
    { errorMsg: 'Failed to mark job as completed!' }
  );

  if (!data) return false;

  const message = data?.msg || data?.message;
  const isKnownError = COMPLETE_ERROR_PATTERNS.some(pattern => message?.includes(pattern));

  if (data?.success === false || isKnownError) {
    toast.error(message || 'Failed to mark job as completed!');
    return false;
  }

  toast.success(message || 'Job marked as completed successfully!');
  if (onSuccess) onSuccess();
  return true;
};
